import { observable, makeObservable, computed, action, runInAction } from 'mobx';
import { FullUser, InputEmployer, UserType } from 'types/userService';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { inviteToSelfEnroll, updateEmployer } from 'api/operations/userService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { EmployerFormFields, getEmployerFormFields } from './inviteEmployersDelegate';

interface EmployerDelegateProps {
  employer: FullUser;
  groupId: string;
  setIsLoading: (isLoading: boolean) => void;
  refreshData: () => Promise<void>;
  isAdmin?: boolean;
}

export class EmployerDelegate {
  private _employer: FullUser;
  private _groupId: string;
  private _setIsLoading;
  private _refreshData;
  private _editModeOn = false;
  private _isAdmin = false;

  public readonly editEmployerForm: FormController<EmployerFormFields> = new FormController(
    {
      ...getEmployerFormFields(),
      email: new FormControl<string>('', { disabled: true }),
    },
    {
      onSubmit: this.editEmployerInfo.bind(this),
    },
  );

  public get employer(): FullUser {
    return this._employer;
  }

  public get employerFullName(): string {
    return `${this._employer.firstName}${this._employer.middleName ? ` ${this._employer.middleName}` : ''}${
      this._employer.lastName ? ` ${this._employer.lastName}` : ''
    }`;
  }

  public get editModeOn(): boolean {
    return this._editModeOn;
  }

  public get isAdmin(): boolean {
    return this._isAdmin;
  }

  public setEditModeOn(editModeOn: boolean): void {
    this._editModeOn = editModeOn;
  }

  public async editEmployerInfo(): Promise<void> {
    await runInAction(async () => {
      const { email: _, ...employerInfo } = this.editEmployerForm.getFormDataObject();
      const updatedEmployerInfo = {
        ...employerInfo,
        id: this._employer.id,
        groupId: this._groupId,
      } as unknown as InputEmployer;

      await updateEmployer(updatedEmployerInfo);
      this.setEditModeOn(false);
      void this._refreshData();
    });
  }

  public async reinviteEmployer(): Promise<void> {
    this._setIsLoading(true);
    const { firstName = '', middleName = '', lastName = '', email = '', title = '', telephone = '' } = this._employer;
    const userInfo = {
      firstName,
      middleName,
      lastName,
      email,
      title,
      groupId: this._groupId,
      telephone,
      userType: 'EMPLOYER' as UserType,
    };
    await inviteToSelfEnroll([userInfo]);
    runInAction(() => {
      this._setIsLoading(false);
      toastManager.addToast(`${firstName} ${lastName} was successfully reinvited`, ToastColorTypes.SUCCESS);
    });
  }

  constructor({ employer, setIsLoading, refreshData, groupId, isAdmin = false }: EmployerDelegateProps) {
    this._employer = employer;
    this.editEmployerForm.populateFields(employer);
    this._groupId = groupId;
    this._setIsLoading = setIsLoading;
    this._refreshData = refreshData;
    this._isAdmin = isAdmin;
    makeObservable<EmployerDelegate, '_employer' | '_editModeOn' | '_groupId'>(this, {
      _employer: observable,
      _groupId: observable,
      _editModeOn: observable,
      editEmployerForm: observable,

      employer: computed,
      editModeOn: computed,
      employerFullName: computed,

      setEditModeOn: action.bound,
      reinviteEmployer: action.bound,
    });
  }
}
